import { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Turnstile } from '@marsidev/react-turnstile';
import { MapPin, Clock, Calendar, Globe, ChevronRight, ChevronDown, Music, Pencil, Trash2, Link2, TriangleAlert } from 'lucide-react';
import axios from '../api/axios';
import { isAxiosError } from 'axios';
import type { Event } from '../types/Event';
// AdSense temporarily disabled pending site validation, see frontend/.env
// import AdSenseBanner from './AdSenseBanner';
import { trackEventReported, trackDirectionsClick, trackEvent } from '../utils/analytics';
import { describeCron } from '../utils/cronDescription';
import ConfirmModal, { type ConfirmModalAction } from './ConfirmModal';

interface EventReport {
  id: string;
  eventId: string;
  message: string;
  createdAt: string;
}

interface EventPopupProps {
  event: Event;
  isLoggedIn: boolean;
  isModOrAdmin?: boolean;
  onSave: (updatedEvent: Event) => Promise<void>;
  onDelete: (id: string, scope?: 'instance' | 'series') => void;
  onOpenImagePreview?: (imageUrl: string, title: string) => void;
  onReportCountChange?: (eventId: string, count: number) => void;
}

function buildShareUrl(eventId: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set('event', eventId);
  return url.toString();
}

const EventPopup: React.FC<EventPopupProps> = ({ event, isLoggedIn, isModOrAdmin, onSave, onDelete, onOpenImagePreview, onReportCountChange }) => {
  const map = useMap();
  const popupRootRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [formData, setFormData] = useState<Event>(event);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ title: string; message: string; actions: ConfirmModalAction[] } | null>(null);
  // When the map listing uses slim=true, description is omitted.
  // We fetch it lazily on first popup open.
  const [description, setDescription] = useState<string | undefined>(event.description);
  const [isLoadingDescription, setIsLoadingDescription] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const DESCRIPTION_MAX_CHARS = 100;
  const isDescriptionTruncated = (description?.length ?? 0) > DESCRIPTION_MAX_CHARS;

  // Reports state
  const [reports, setReports] = useState<EventReport[]>([]);
  const [reportMessage, setReportMessage] = useState('');
  const [isReportFormOpen, setIsReportFormOpen] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    try {
      const res = await axios.get(`/events/${event.id}/reports`);
      setReports(res.data);
      onReportCountChange?.(event.id, res.data.length);
    } catch {
      // non-critical
    }
  }, [event.id, onReportCountChange]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Fetch description lazily if it was omitted in the slim map listing.
  useEffect(() => {
    if (event.description !== undefined) {
      setDescription(event.description);
      return;
    }
    setIsLoadingDescription(true);
    axios.get<Event>(`/events/${event.id}`)
      .then(res => {
        const desc = res.data.description ?? '';
        setDescription(desc);
        setFormData(prev => ({ ...prev, description: desc }));
      })
      .catch(() => setDescription(''))
      .finally(() => setIsLoadingDescription(false));
  }, [event.id, event.description]);


  useEffect(() => {
    setFormData(event);
    setIsEditing(false);
    setImageFile(null);
    setImagePreview(null);
  }, [event]);

  useEffect(() => {
    const root = popupRootRef.current;
    if (!root) {
      return;
    }

    // Leaflet listens to native mouse events on map/popup containers; this prevents popup-closing side effects.
    L.DomEvent.disableClickPropagation(root);
    L.DomEvent.disableScrollPropagation(root);
  }, []);

  const toDateTimeLocalValue = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  const normalizeUrl = (url?: string | null) => {
    if (!url) {
      return null;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return `https://${url}`;
  };

  const getActivePopupMarker = useCallback((): L.Marker | null => {
    const popup = (map as unknown as { _popup?: { _source?: L.Marker } })._popup;
    return popup?._source ?? null;
  }, [map]);

  const reverseGeocodeLocation = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return (data?.display_name as string | undefined) ?? null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const marker = getActivePopupMarker();
    if (!marker?.dragging) {
      return;
    }

    marker.dragging.enable();

    const handleDragEnd = async () => {
      const latLng = marker.getLatLng();
      const nextLat = latLng.lat;
      const nextLng = latLng.lng;
      const nextLocation = await reverseGeocodeLocation(nextLat, nextLng);
      marker.openPopup();

      setFormData((prev) => ({
        ...prev,
        lat: nextLat,
        lng: nextLng,
        location: nextLocation || prev.location,
      }));
    };

    marker.on('dragend', handleDragEnd);

    return () => {
      marker.off('dragend', handleDragEnd);
      marker.dragging?.disable();
    };
  }, [isEditing, map, getActivePopupMarker]);

  const resetMarkerPositionToSaved = () => {
    const marker = getActivePopupMarker();
    if (!marker?.setLatLng) {
      return;
    }

    marker.setLatLng([event.lat, event.lng]);
  };

  const clearImageFile = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setFormData((prev) => ({ ...prev, imageUrl: null }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditMapMarkerDragEnd = async (e: L.LeafletEvent) => {
    const marker = e.target as L.Marker;
    const { lat, lng } = marker.getLatLng();
    setFormData((prev) => ({ ...prev, lat, lng }));
    const nextLocation = await reverseGeocodeLocation(lat, lng);
    if (nextLocation) setFormData((prev) => ({ ...prev, location: nextLocation }));
  };

  const uploadImageToS3 = async () => {
    if (!imageFile) {
      return formData.imageUrl ?? null;
    }

    setIsUploadingImage(true);

    try {
      const payload = new FormData();
      payload.append('file', imageFile);
      payload.append('type', 'confirmed');

      const response = await axios.post('/upload', payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return (response.data?.url as string | undefined) ?? null;
    } catch (error) {
      console.error('Error uploading image to S3:', error);
      alert('Nie udało się wysłać zdjęcia do S3');
      throw error;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (formData.recurring && formData.recurrenceCron && formData.recurrencePattern) {
      alert('Podaj albo wyrażenie cron, albo słowny opis cyklu — nie oba naraz.');
      return;
    }

    setIsSaving(true);

    try {
      const uploadedImageUrl = await uploadImageToS3();

      const updatedEvent: Event = {
        ...formData,
        imageUrl: uploadedImageUrl,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        recurrenceStartDate: formData.recurring && formData.recurrenceStartDate
          ? new Date(formData.recurrenceStartDate).toISOString()
          : null,
        recurrenceEndDate: formData.recurring && formData.recurrenceEndDate
          ? new Date(formData.recurrenceEndDate).toISOString()
          : null,
        tags: formData.tags.map((tag) => tag.trim()).filter(Boolean),
      };

      await onSave(updatedEvent);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating event from popup:', error);
      alert('Nie udało się zaktualizować wydarzenia');
    } finally {
      setIsSaving(false);
    }
  };

  const openImagePreview = () => {
    if (!event.imageUrl || !onOpenImagePreview) {
      return;
    }

    onOpenImagePreview(event.imageUrl, event.title);
  };

  const isSameCalendarDay = (a: string, b: string) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA.toDateString() === dateB.toDateString();
  };

  return (
    <>
      <div
        ref={popupRootRef}
        id="event-popup-root"
        className={`relative transition-all duration-200 ${isEditing ? 'w-[320px] max-w-[460px]' : 'w-[320px] max-w-[320px]'}`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!isEditing ? (
          <>
            <div id="event-popup-header" className="mb-3 flex items-start gap-3">
              {event.imageUrl ? (
                <button
                  id="event-popup-image-button"
                  type="button"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    openImagePreview();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();

                    // Keyboard activation triggers click without a pointerdown event.
                    if (e.detail === 0) {
                      openImagePreview();
                    }
                  }}
                  className="relative block h-14 w-14 shrink-0 cursor-zoom-in overflow-hidden rounded-full"
                  aria-label="Powiększ zdjęcie wydarzenia"
                >
                  <img
                    id="event-popup-image"
                    src={event.imageUrl}
                    alt={event.title}
                    className="h-14 w-14 rounded-full object-cover"
                  />
                  <span
                    id="event-popup-magnifying-glass-trigger"
                    className="pointer-events-none absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/80 bg-black/60 text-white"
                    aria-hidden="true"
                  >
                    <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden="true">
                      <line x1="7" y1="1" x2="7" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <line x1="1" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>
              ) : (
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/20 text-[var(--accent)]">
                  <Music size={22} strokeWidth={2} aria-hidden="true" />
                  <span className="sr-only">Brak fotografii</span>
                </span>
              )}
              <div className="min-w-0 flex-1 pt-0.5 text-left">
                <p id="event-popup-title" className="leading-tight">
                  <span id="event-popup-title-label" className="sr-only">Tytuł wydarzenia:</span>
                  <span className="block text-lg font-bold text-white">{event.title}</span>
                </p>
                <div className="mt-0.5">
                  <p
                    id="event-popup-description"
                    className={`text-sm leading-5 transition-colors duration-300 ease-in-out ${isDescriptionExpanded ? 'text-slate-100' : 'text-slate-400'}`}
                  >
                    <span id="event-popup-description-label" className="sr-only">Opis:</span>
                    {isLoadingDescription
                      ? 'Ładowanie...'
                      : isDescriptionExpanded || !isDescriptionTruncated
                        ? description
                        : `${description?.slice(0, DESCRIPTION_MAX_CHARS).trimEnd()}…`}
                  </p>
                  {isDescriptionTruncated && (
                    <button
                      type="button"
                      id="event-popup-description-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDescriptionExpanded((prev) => !prev);
                      }}
                      className="mt-1 flex items-center gap-1 text-xs font-semibold text-[var(--accent)] hover:text-[var(--accent-strong)]"
                    >
                      {isDescriptionExpanded ? 'Zwiń opis' : 'Rozwiń opis'}
                      <ChevronDown
                        size={14}
                        strokeWidth={2.5}
                        className={`transition-transform duration-300 ${isDescriptionExpanded ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-3 border-t border-white/10" />

            {/* <AdSenseBanner /> */}
            {event.free === false && (
              <div className="mb-3">
                <span className="sep-paid-badge">🎟 Wstęp płatny</span>
                {event.ticketUrl && (
                  <p className="mt-1 text-sm text-slate-300">
                    <span className="uppercase tracking-wide !font-[900] !text-base !text-slate-100">Kup bilety:</span>{' '}
                    <a
                      href={normalizeUrl(event.ticketUrl) ?? '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="popup-yellow-link underline break-all"
                      aria-label="Przejdź do strony zakupu biletów"
                      onClick={() => trackEvent('ticket_link_click', { event_id: event.id })}
                    >
                      {event.ticketUrl}
                    </a>
                  </p>
                )}
              </div>
            )}

            <div className="mb-3 space-y-3">
              <div id="event-popup-location" className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                  <MapPin size={16} strokeWidth={2.25} aria-hidden="true" />
                </span>
                <div className="min-w-0 text-left">
                  <span id="event-popup-location-label" className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Lokalizacja:</span>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${event.lat},${event.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="popup-yellow-link underline decoration-dotted text-sm font-semibold"
                    onClick={() => trackDirectionsClick(event.id)}
                  >
                    {event.location}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                  <Calendar size={16} strokeWidth={2.25} aria-hidden="true" />
                </span>
                <div className="min-w-0 text-left">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Termin:</span>
                  <p className="text-sm font-semibold text-white">
                    {new Date(event.startTime).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-xs text-slate-200">
                    <Clock size={12} strokeWidth={2.5} aria-hidden="true" />
                    <span id="event-popup-start-time-label" className="sr-only">Czas rozpoczęcia:</span>
                    <span>{new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <span aria-hidden="true">→</span>
                    <span id="event-popup-end-time-label" className="sr-only">Czas zakończenia:</span>
                    <span>
                      {isSameCalendarDay(event.startTime, event.endTime)
                        ? new Date(event.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : new Date(event.endTime).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {event.url ? (
                <a
                  href={normalizeUrl(event.url) ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Otwórz stronę internetową wydarzenia w nowej karcie"
                  title="Otwórz stronę wydarzenia"
                  className="-mx-1 flex items-center gap-2.5 rounded-lg px-1 py-0.5 hover:bg-white/5"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                    <Globe size={16} strokeWidth={2.25} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 text-left">
                    <span id="event-popup-url-label" className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Strona internetowa:</span>
                    <span className="popup-yellow-link block truncate text-sm font-semibold underline">{event.url}</span>
                  </span>
                  <ChevronRight size={16} className="shrink-0 text-slate-500" aria-hidden="true" />
                </a>
              ) : (
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                    <Globe size={16} strokeWidth={2.25} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 text-left">
                    <span id="event-popup-url-label" className="block text-[11px] font-bold uppercase tracking-wide text-slate-400">Strona internetowa:</span>
                    <span className="text-sm text-slate-300">Brak strony internetowej</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3 border-t border-white/10" />

            {isLoggedIn ? (
              <div className="mb-2 space-y-2">
                <button
                  id="edit-event-button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsEditing(true);
                  }}
                  className="btn-action btn-blue flex w-full items-center justify-center gap-1.5"
                >
                  <Pencil size={14} strokeWidth={2.5} aria-hidden="true" />
                  Edytuj
                </button>
                <div className="flex gap-2">
                  <button
                    id="event-popup-delete-button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const doDelete = (scope?: 'instance' | 'series') => {
                        if (scope) {
                          onDelete(event.id, scope);
                        } else {
                          onDelete(event.id);
                        }
                        map.closePopup();
                        setDeleteConfirm(null);
                      };
                      if (event.recurring) {
                        setDeleteConfirm({
                          title: 'Usuń wydarzenie cykliczne',
                          message: 'To wydarzenie jest częścią cyklu. Co chcesz zrobić?',
                          actions: [
                            { label: 'Usuń cały cykl', variant: 'red', onClick: () => doDelete('series') },
                            { label: 'Usuń tylko to wystąpienie', variant: 'red', onClick: () => doDelete('instance') },
                            { label: 'Anuluj', variant: 'gray', onClick: () => setDeleteConfirm(null) },
                          ],
                        });
                      } else {
                        setDeleteConfirm({
                          title: 'Usuń wydarzenie',
                          message: 'Czy na pewno chcesz usunąć to wydarzenie?',
                          actions: [
                            { label: 'Usuń', variant: 'red', onClick: () => doDelete() },
                            { label: 'Anuluj', variant: 'gray', onClick: () => setDeleteConfirm(null) },
                          ],
                        });
                      }
                    }}
                    className="btn-action btn-red flex items-center justify-center gap-1.5"
                  >
                    <Trash2 size={14} strokeWidth={2.5} aria-hidden="true" />
                    Usuń
                  </button>
                  <button
                    id="share-event-button"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const shareUrl = buildShareUrl(event.id);
                      navigator.clipboard.writeText(shareUrl).then(() => {
                        setShareCopied(true);
                        setTimeout(() => setShareCopied(false), 2500);
                      });
                    }}
                    className="btn-action btn-turquoise flex items-center justify-center gap-1.5"
                  >
                    <Link2 size={14} strokeWidth={2.5} aria-hidden="true" />
                    {shareCopied ? '✓ Skopiowano link!' : 'Skopiuj odnośnik'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-2">
                <button
                  id="share-event-button"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    const shareUrl = buildShareUrl(event.id);
                    navigator.clipboard.writeText(shareUrl).then(() => {
                      setShareCopied(true);
                      setTimeout(() => setShareCopied(false), 2500);
                    });
                  }}
                  className="btn-action btn-turquoise flex w-full items-center justify-center gap-1.5"
                >
                  <Link2 size={14} strokeWidth={2.5} aria-hidden="true" />
                  {shareCopied ? '✓ Skopiowano link!' : 'Skopiuj odnośnik'}
                </button>
              </div>
            )}

            {!reportSuccess && !isReportFormOpen && (
              <button
                id="event-popup-report-button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsReportFormOpen(true);
                  setReportSuccess(false);
                  setReportError(null);
                  setTurnstileToken(null);
                }}
                className="mb-2 flex w-full items-center gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-left hover:bg-amber-500/15"
              >
                <TriangleAlert size={18} className="shrink-0 text-amber-400" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-amber-200">Zgłoś nieprawidłowość</span>                </span>
                <ChevronRight size={16} className="shrink-0 text-amber-400/70" aria-hidden="true" />
              </button>
            )}

            {/* ── Reports / Comments section ── */}
            <div id="event-popup-reports-section" className="mt-4 pt-1">
              {/* Reports list — visible only to mods/admins */}
              {isModOrAdmin && (
                <>
                  {event.createdByName && (
                    <p id="event-popup-created-by" className="mb-2">
                      <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Dodane/zatwierdzone przez:</span>
                      <span className="text-sm font-semibold text-white">{event.createdByName}</span>
                    </p>
                  )}
                  <p id="event-popup-reports-title" className="text-[11px] font-bold uppercase tracking-wide text-slate-200 mb-2">
                    Zgłoszenia nieprawidłowości
                    {reports.length > 0 && (
                      <span id="event-popup-report-count" className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold px-2 py-0.5">
                        ({reports.length})
                      </span>
                    )}
                  </p>

                  {reports.length === 0 ? (
                    <p className="text-sm text-slate-400 mb-2">Brak zgłoszeń.</p>
                  ) : (
                    <ul className="space-y-2 mb-3 max-h-40 overflow-y-auto pr-1">
                      {reports.map((r) => (
                        <li key={r.id} className="rounded-lg bg-slate-900/50 px-3 py-2 text-sm text-slate-200">
                          <div id="event-popup-user-comment" className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="break-words">{r.message}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{new Date(r.createdAt).toLocaleString()}</p>
                            </div>
                            <button
                              type="button"
                              aria-label="Usuń zgłoszenie"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await axios.delete(`/events/${event.id}/reports/${r.id}`);
                                  await fetchReports();
                                } catch {
                                  alert('Nie udało się usunąć zgłoszenia.');
                                }
                              }}
                              style={{ backgroundColor: '#ef4444', border: '1.5px solid white', borderRadius: '6px' }}
                              className="shrink-0 flex items-center justify-center w-10 h-10 transition-opacity hover:opacity-80"
                            >
                              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14H6L5 6" />
                                <path d="M10 11v6" />
                                <path d="M14 11v6" />
                                <path d="M9 6V4h6v2" />
                              </svg>
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {/* Report form — visible to everyone */}
              {reportSuccess ? (
                <p className="text-sm text-green-400 mt-1">✓ Zgłoszenie wysłane. Dziękujemy!</p>
              ) : !isReportFormOpen ? null : (
                <div className="mt-1">
                  <>
                    <textarea
                      value={reportMessage}
                      onChange={(e) => setReportMessage(e.target.value)}
                      placeholder="Opisz nieprawidłowość (max 100 znaków)..."
                      maxLength={100}
                      rows={3}
                      className="modal-textarea text-sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div id="event-popup-report-turnstile" className="mt-2" onClick={(e) => e.stopPropagation()}>
                      <Turnstile
                        siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '1x00000000000000000000AA'}
                        onSuccess={setTurnstileToken}
                        onExpire={() => setTurnstileToken(null)}
                        onError={() => setTurnstileToken(null)}
                        options={{ theme: 'dark', size: 'flexible' }}
                      />
                    </div>
                    {reportError && <p className="text-xs text-red-400 mt-1">{reportError}</p>}
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        disabled={isSubmittingReport || !turnstileToken}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!reportMessage.trim()) {
                            setReportError('Treść zgłoszenia jest wymagana.');
                            return;
                          }
                          setIsSubmittingReport(true);
                          setReportError(null);
                          try {
                            await axios.post(`/events/${event.id}/reports`, { message: reportMessage.trim(), cf_turnstile_token: turnstileToken });
                            setReportMessage('');
                            setReportSuccess(true);
                            setIsReportFormOpen(false);
                            setTurnstileToken(null);
                            trackEventReported(event.id);
                            await fetchReports();
                          } catch (err) {
                            const message = isAxiosError(err) ? err.response?.data?.error : undefined;
                            setReportError(message || 'Nie udało się wysłać zgłoszenia.');
                          } finally {
                            setIsSubmittingReport(false);
                          }
                        }}
                        className="btn-action btn-amber"
                        style={{ fontSize: '12px', padding: '4px 12px' }}
                      >
                        {isSubmittingReport ? 'Wysyłanie...' : 'Wyślij'}
                      </button>
                      <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsReportFormOpen(false);
                            setReportMessage('');
                            setReportError(null);
                            setTurnstileToken(null);
                          }}
                          className="btn-action"
                          style={{ fontSize: '12px', padding: '4px 12px', background: 'rgba(255,255,255,0.08)' }}
                        >
                          Anuluj
                        </button>
                      </div>
                  </>
                </div>
              )}
            </div>
          </>
        ) : (
          <div>
            <label htmlFor="event-popup-edit-title" className="modal-label">Tytuł:</label>
            <input
              id="event-popup-edit-title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="modal-input"
            />
            <label htmlFor="event-popup-edit-description" className="modal-label">Opis:</label>
            <textarea
              id="event-popup-edit-description"
              value={formData.description ?? ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="modal-textarea"
            />
            <label htmlFor="event-popup-edit-location" className="modal-label">Lokalizacja:</label>
            <input
              id="event-popup-edit-location"
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="modal-input"
            />
            <div id="event-popup-edit-coordinates" className="grid grid-cols-2 gap-2">
              <div id="event-popup-edit-lng-container">
                <label htmlFor="event-popup-edit-lng" className="modal-label">Długość geo.:</label>
                <input
                  id="event-popup-edit-lng"
                  type="number"
                  step="any"
                  value={formData.lng}
                  onChange={(e) => setFormData({ ...formData, lng: Number(e.target.value) })}
                  className="modal-input"
                />
              </div>
              <div id="event-popup-edit-lat-container">
                <label htmlFor="event-popup-edit-lat" className="modal-label">Szerokość geo.:</label>
                <input
                  id="event-popup-edit-lat"
                  type="number"
                  step="any"
                  value={formData.lat}
                  onChange={(e) => setFormData({ ...formData, lat: Number(e.target.value) })}
                  className="modal-input"
                />
              </div>
            </div>
            <label className="modal-label">Lokalizacja (przeciągnij pinezkę):</label>
            <div id="event-popup-edit-map" className="h-[300px] rounded-lg overflow-hidden border border-white/20 mb-2 mt-2">
              <MapContainer center={[formData.lat, formData.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution=""
                />
                <Marker
                  position={[formData.lat, formData.lng]}
                  draggable
                  eventHandlers={{ dragend: handleEditMapMarkerDragEnd }}
                />
              </MapContainer>
            </div>
            <p className="text-xs text-slate-300 mb-2">
              Przeciągnij pinezkę na mapie — długość, szerokość i miejsce zaktualizują się automatycznie.
            </p>
            <label className="modal-checkbox-label">
              <input
                id="event-popup-edit-recurring"
                type="checkbox"
                checked={!!formData.recurring}
                onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
              />
              <span className="modal-label !mb-0">Wydarzenie cykliczne</span>
            </label>
            {formData.recurring && (
              <div id="event-popup-edit-recurrence-details">
                <label htmlFor="event-popup-edit-recurrence-start-date" className="modal-label">Cykl obowiązuje od:</label>
                <input
                  id="event-popup-edit-recurrence-start-date"
                  type="datetime-local"
                  value={toDateTimeLocalValue(formData.recurrenceStartDate ?? '')}
                  onChange={(e) => setFormData({ ...formData, recurrenceStartDate: e.target.value || null })}
                  className="modal-input"
                />
                <label htmlFor="event-popup-edit-recurrence-end-date" className="modal-label mt-2">Cykl obowiązuje do (opcjonalnie):</label>
                <input
                  id="event-popup-edit-recurrence-end-date"
                  type="datetime-local"
                  value={toDateTimeLocalValue(formData.recurrenceEndDate ?? '')}
                  onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value || null })}
                  className="modal-input"
                />
                <label htmlFor="event-popup-edit-recurrence-cron" className="modal-label mt-2">Wyrażenie cron (opcjonalnie):</label>
                <input
                  id="event-popup-edit-recurrence-cron"
                  type="text"
                  value={formData.recurrenceCron ?? ''}
                  disabled={!!formData.recurrencePattern}
                  onChange={(e) => setFormData({ ...formData, recurrenceCron: e.target.value || null })}
                  placeholder="np. 0 9 * * 6"
                  className="modal-input disabled:opacity-50"
                />
                {formData.recurrenceCron && describeCron(formData.recurrenceCron) && (
                  <p className="mt-1 text-xs text-slate-300">{describeCron(formData.recurrenceCron)}</p>
                )}
                <label htmlFor="event-popup-edit-recurrence-pattern" className="modal-label mt-2">Cykl (słowny opis, opcjonalnie):</label>
                <input
                  id="event-popup-edit-recurrence-pattern"
                  type="text"
                  value={formData.recurrencePattern ?? ''}
                  disabled={!!formData.recurrenceCron}
                  onChange={(e) => setFormData({ ...formData, recurrencePattern: e.target.value || null })}
                  placeholder="np. Każda sobota o godzinie 9.00"
                  className="modal-input disabled:opacity-50"
                />
                <p className="mt-1 text-xs text-slate-300">Podaj wyrażenie cron albo słowny opis — nie oba naraz.</p>
              </div>
            )}
            {!formData.recurring && (
              <>
                <label className="modal-label">Czas rozpoczęcia:</label>
                <input
                  id="event-popup-edit-start-time"
                  type="datetime-local"
                  value={toDateTimeLocalValue(formData.startTime)}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  className="modal-input"
                />
                <label className="modal-label">Czas zakończenia:</label>
                <input
                  id="event-popup-edit-end-time"
                  type="datetime-local"
                  value={toDateTimeLocalValue(formData.endTime)}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  className="modal-input"
                />
              </>
            )}
            <label htmlFor="event-popup-edit-url" className="modal-label">Strona internetowa:</label>
            <input
              id="event-popup-edit-url"
              type="url"
              value={formData.url ?? ''}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="modal-input"
              placeholder="https://..."
            />
            <label htmlFor="event-popup-edit-image-url" className="modal-label">URL do zdjęcia poglądowego:</label>
            <input
              id="event-popup-edit-image-url"
              type="url"
              value={formData.imageUrl ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, imageUrl: value || null });
                if (value) clearImageFile();
              }}
              className="modal-input"
              placeholder="https://..."
              disabled={!!imageFile}
            />
            <label htmlFor="event-popup-edit-image-file" className="modal-label">Zdjęcie poglądowe:</label>
            {/* Mutually exclusive with the image URL field above - only one image source is allowed. */}
            <input
              id="event-popup-edit-image-file"
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageFileChange}
              className="modal-input"
              disabled={!!formData.imageUrl}
            />
            {imagePreview && (
              <div id="event-popup-edit-image-preview-container" className="relative mt-2 w-full h-24">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-24 object-cover rounded"
                />
                <button
                  type="button"
                  onClick={clearImageFile}
                  aria-label="Usuń zdjęcie"
                  className="modal-close-btn absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full border text-white"
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                    <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            )}
            
            <label htmlFor="event-popup-edit-tags" className="modal-label">Tagi (przecinek jako separator):</label>
            <input
              id="event-popup-edit-tags"
              type="text"
              value={formData.tags.join(', ')}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map((tag) => tag.trim()) })}
              className="modal-input"
              placeholder="tag1, tag2"
            />
            <label className="modal-checkbox-label">
              <input
                id="event-popup-edit-free"
                type="checkbox"
                checked={!!formData.free}
                onChange={(e) => setFormData({ ...formData, free: e.target.checked })}
              />
              <span className="modal-label !mb-0">Wstęp wolny</span>
            </label>
            {!formData.free && (
              <>
                <label htmlFor="event-popup-edit-ticket-url" className="modal-label">Link do biletów:</label>
                <input
                  id="event-popup-edit-ticket-url"
                  type="url"
                  value={formData.ticketUrl ?? ''}
                  onChange={(e) => setFormData({ ...formData, ticketUrl: e.target.value || null })}
                  className="modal-input"
                  placeholder="https://..."
                />
              </>
            )}
            <div id="event-popup-edit-actions" className="modal-btn-row">
              <button
                id="event-popup-cancel-edit"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  resetMarkerPositionToSaved();
                  setFormData(event);
                  setIsEditing(false);
                  clearImageFile();
                }}
                disabled={isSaving || isUploadingImage}
                className="btn-action btn-red"
              >
                Odrzuć
              </button>
              <button
                id="event-popup-save-edit"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void handleSave();
                }}
                disabled={isSaving || isUploadingImage}
                className="btn-action btn-green"
              >
                {isUploadingImage ? 'Wysyłanie zdjęcia...' : isSaving ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        )}
      </div>
      {deleteConfirm && (
        <ConfirmModal
          title={deleteConfirm.title}
          message={deleteConfirm.message}
          actions={deleteConfirm.actions}
          onDismiss={() => setDeleteConfirm(null)}
        />
      )}
    </>
  );
};

export default EventPopup;
